import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable, throwError } from 'rxjs';
import { Submission } from './models/submission.model';
import { ActionService } from '../shared/_services/action.service';
import { ActionResponse } from '../shared/_models/action-response.model';
import { UploadIRS } from './models/upload-irs.model';
import { ApiResponse } from './models/api-response.model';
import { GtwElementsBaseUrlsModel } from '../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../global/services/json-object-factory.service';


enum URLS {
  PING_IRS='/pingServer',
  SUBMIT_TO_IRS='/submit',
  // Per-row IRS submit driven from the IRS Submissions Status grid's
  // "Send To IRS" action (EfileService.js L270-279). Differs from
  // `/submit` in that it accepts an `irsId` so a previously prepared
  // submission can be transmitted without re-uploading.
  SUBMIT_VALIDATED_RETURN='/submitValidatedReturn',
  POLL_ACK='/pollAck',
  XML_UPLOAD_KEY_ID='/xmlUploadKeyId',
  BULK_XML_SYNC='/copyFederalXMLtoS3',
  // Legacy EfileFactory endpoints (ui/app/components/efile/EfileService.js L365-432).
  // Used by the submissions-list `ctrlFn` confirm-action flow (validate pencil
  // on the Return Error column + bulk toolbar actions).
  VALIDATE='/validate',
  VALIDATE_BATCH='/validateBatch',
  GENERATE_BATCH='/generateBatch',
  POLL_ACK_BATCH='/pollAckBatch',
  DOWNLOAD_BATCH_ACKS='/downloadSelectedAckg',
  SUBMIT_BATCH='/submitBatch',
  // Legacy EfileFactory PDF download endpoints
  // (ui/app/components/efile/EfileService.js L556-L572). Triggered from the
  // submission-detail sidebar's "Download 8453 Pdf" / "Download 8879 Pdf"
  // entries (efile.js does not expose these as ui-router states; they are
  // raw EfileFactory calls bound to sidebar buttons in legacy markup).
  GENERATE_8453C_PDF='/Generate8453CPdfContent',
  GENERATE_8879C_PDF='/Generate8879CPdfContent',
  // Legacy EfileFactory submission-XML generator
  // (ui/app/components/efile/EfileService.js L609 — `generateSubmissionXML`).
  // Used by EfileKeyXmlGenController#generateSubmissionXML.
  GENERATE_XML_INFO='/generateXMLInfo'
}

@Injectable({
  providedIn: 'root'
})
export class EFileService {

  /**
   * Mirrors legacy `efileFactory.ENV_FOR_PROCESS_SPLIT`
   * (`ui/app/components/efile/EfileService.js` L20). The environment that
   * triggers the "Prepare Submission" / signed-tax-doc upload flow ahead
   * of an IRS submission (vs the immediate `Submit` flow for TEST). Lives
   * on the service so every consumer reads it from a single source —
   * `submit-action`, `req-header-elements`, and any future component that
   * needs to gate behaviour on the split env.
   */
  readonly ENV_FOR_PROCESS_SPLIT = 'PRODUCTION' as const;

  baseURLs: GtwElementsBaseUrlsModel = {
    api: '/gtw',
    custom: '/custom',
    admin: '/admin',
    efile:'/gtw-efile-api',
    pdfEngine: '/pdf-engine',
    bulkPdfEngine: '/bulk-pdf-engine'
  };

  constructor(private httpClient: HttpClient,
    private actionService: ActionService,
    private jsonObjectFactory: JsonObjectFactoryService) { }

  setBaseUrls(baseURLs: GtwElementsBaseUrlsModel){
    this.baseURLs = baseURLs;
  }

  /**
   * Legacy `submissions-dasboard.submission` resolve parity.
   * AngularJS loaded a submission by id with action_code `nvc2sx` before
   * rendering any child state, then seeded `EfileFactory.selectedSubmission`.
   */
  getSubmissionById(baseUrl: string, submissionId: string | number): Observable<Submission | null> {
    const url = `${baseUrl}/loadJsonObject`;
    return this.jsonObjectFactory.getJSONObj(
      url,
      {
        action_code: 'nvc2sx',
        submission_id: String(submissionId ?? ''),
      },
      undefined,
      undefined,
      { silent: true },
    ).pipe(
      map((response: any) => {
        const rows = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
        return rows.length > 0 ? rows[0] as Submission : null;
      })
    );
  }

  getSubmissionData(clientKey: number, taxYear: string, submissionType?: string): Observable<Submission[]> {

    const fromObject: Record<string, string> = {
      'tax_year': taxYear
    };
    if (submissionType) {
      fromObject['type'] = submissionType;
    }
    const params = new HttpParams({ fromObject });
    return this.actionService.getActionData(this.baseURLs.api,
      32096,
      clientKey,
      params
    ).pipe(
      map((data: ActionResponse) => {
        if(data.jsonObject === null){
          throw throwError(data); 
        }
        return data.jsonObject;
      })
    );
  }

  uploadIRSData(clientKey: number, taxYear: string): Observable<UploadIRS[]> {

    const params = new HttpParams({
      fromObject:{
        'tax_year':taxYear
      }
    });
    return this.actionService.getActionData(this.baseURLs.api,
      32090,
      clientKey,
      params
    ).pipe(
      map((data: ActionResponse) => {
        return data.jsonObject;
      })
    );
  }
  pingIrsServer(clientKey: number, envType: string): Observable<ApiResponse> {

    const headers = new HttpHeaders();
    // headers.append('client_key',clientKey.toString());

    const params = new HttpParams({
      fromObject:{
        'envType':envType
      }
    });
    return this.httpClient.get<ApiResponse>(`${this.baseURLs.efile}${URLS.PING_IRS}`,{
      params:params,
      headers:headers
    });
  }
  submitToIRS(clientKey: number, envType: string,submissionId:string): Observable<ApiResponse> {

    const headers = new HttpHeaders();
    // headers.append('client_key',clientKey.toString());

    const params = new HttpParams({
      fromObject:{
        'env':envType,
        'subId':submissionId
      }
    });
    return this.httpClient.get<ApiResponse>(`${this.baseURLs.efile}${URLS.SUBMIT_TO_IRS}`,{
      params:params,
      headers:headers
    });
  }

  /**
   * GET `{efile}/submitValidatedReturn?env&subId&irsId` — port of
   * `efileFactory.submitValidatedReturn` (EfileService.js L270-279).
   * Invoked from the IRS Submissions Status grid's per-row "Send To IRS"
   * action (legacy `vm.sendToIRS` → `$rootScope.$emit('submit-valid-return')`
   * → SubmitController L724-726 → `EfileFactory.submitValidatedReturn`).
   */
  submitValidatedReturn(
    envType: string,
    submissionId: string,
    irsId: string,
  ): Observable<ApiResponse> {
    const params = new HttpParams({
      fromObject: {
        env: envType,
        subId: submissionId,
        irsId,
      },
    });
    return this.httpClient.get<ApiResponse>(
      `${this.baseURLs.efile}${URLS.SUBMIT_VALIDATED_RETURN}`,
      { params },
    );
  }

  /**
   * POST `{api}/saveJsonObject?action_code=6mtt1s` — port of
   * `efileFactory.updateTaxDocUrl` (EfileService.js L669-680).
   * After a PRODUCTION prep submission creates a new IRS submission row,
   * we associate the previously uploaded tax-doc S3 file key with the
   * fresh IRS_ID so the IRS submissions grid can resolve it.
   */
  updateTaxDocUrl(
    baseUrl: string,
    irsId: string,
    ssoId: string,
    fileKey: string,
  ): Observable<ApiResponse> {
    const body = new URLSearchParams();
    body.set('irs_id', irsId);
    body.set('sso_id', ssoId);
    body.set('file_key', fileKey);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.post<ApiResponse>(
      `${baseUrl}/saveJsonObject?action_code=6mtt1s`,
      body.toString(),
      { headers, withCredentials: true },
    );
  }
  pollAck(
    clientKey: number,
    submissionId: string,
    taxYear: string,
    scenario: string,
    jcdKey: string | number,
  ): Observable<ApiResponse> {

    const headers = new HttpHeaders();
    // headers.append('client_key',clientKey.toString());

    const params = new HttpParams({
      fromObject: {
        subId: submissionId,
        taxYear: String(taxYear),
        scenario: String(scenario),
        jcdKey: String(jcdKey),
      },
    });
    return this.httpClient.get<ApiResponse>(`${this.baseURLs.efile}${URLS.POLL_ACK}`, {
      params,
      headers,
    });
  }

  uploadXMLKeyId(submissionId: string, fileName: string, file: File, taxYear: string): Observable<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new HttpParams({
      fromObject: {
        submissionId,
        fileName,
        taxYear
      }
    });

    return this.httpClient.post<ApiResponse>(`${this.baseURLs.efile}${URLS.XML_UPLOAD_KEY_ID}`, formData, {
      params
    });
  }

  syncXML(taxYear: string, submissionId: string, scenario: string, jcdKey: string): Observable<ApiResponse> {
    const params = new HttpParams({
      fromObject: {
        taxYear,
        submissionId,
        scenario,
        jcdKey
      }
    });

    return this.httpClient.get<ApiResponse>(`${this.baseURLs.bulkPdfEngine}${URLS.BULK_XML_SYNC}`, {
      params
    });
  }

  // ---------------------------------------------------------------------------
  // Submissions-list `ctrlFn` actions — legacy EfileFactory parity.
  //
  // These mirror `efileFactory.validate` (L365), `.validateBatch` (L382),
  // `.generateBatch` (L402), `.efileBatchPollAck` (L389), `.downloadBatchAcks`
  // (L395) and `.efileBatchSubmission` (L409) in
  // `ui/app/components/efile/EfileService.js`. The submissions grid pencil
  // icon (Return Error column) dispatches `ctrlFn('validate', rowData, 'form')`
  // which opens the shared confirm-action modal, and then on confirm calls
  // the corresponding EfileFactory method. Angular 12 wires the same flow
  // through `EfileCtrlFnDispatcherService`.
  // ---------------------------------------------------------------------------

  /** POST {efile}/validate?submissionId=X&keyId=&reqType= — single submission.
   *  Legacy sends Content-Type: application/json (EfileService.js L376-378).
   *  Must set header explicitly so FormEncodeInterceptor skips this request
   *  and does NOT convert the JSON body to x-www-form-urlencoded (→ 415).
   */
  validateSubmission(submissionIds: string, keyId = '', taxEntity: any = {}): Observable<ApiResponse> {
    const fromObject: Record<string, string> = {
      submissionId: submissionIds,
      keyId,
      reqType: keyId ? 'PDFGen' : ''
    };
    const params = new HttpParams({ fromObject });
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.post<ApiResponse>(
      `${this.baseURLs.efile}${URLS.VALIDATE}`,
      taxEntity || {},
      { params, headers, withCredentials: true }
    );
  }

  /** GET {efile}/validateBatch?submissionIds=csv — bulk toolbar validate. */
  validateBatch(submissionIds: string): Observable<ApiResponse> {
    const params = new HttpParams({ fromObject: { submissionIds } });
    return this.httpClient.get<ApiResponse>(`${this.baseURLs.efile}${URLS.VALIDATE_BATCH}`, { params });
  }

  /** GET {efile}/generateBatch?submissionIds=csv — bulk XML generation. */
  generateBatch(submissionIds: string): Observable<ApiResponse> {
    const params = new HttpParams({ fromObject: { submissionIds } });
    return this.httpClient.get<ApiResponse>(`${this.baseURLs.efile}${URLS.GENERATE_BATCH}`, { params });
  }

  /**
   * POST {efile}/generateXMLInfo?submissionId=X — direct port of
   * `efileFactory.generateSubmissionXML` (EfileService.js L609). Triggered
   * from the Key XML Gen grid's "Generate Submission XML" toolbar button.
   */
  generateSubmissionXML(submissionId: string): Observable<ApiResponse> {
    const params = new HttpParams({ fromObject: { submissionId } });
    return this.httpClient.post<ApiResponse>(
      `${this.baseURLs.efile}${URLS.GENERATE_XML_INFO}`,
      null,
      { params }
    );
  }

  /** GET {efile}/pollAckBatch?submissionIds=csv — bulk ack poll. */
  pollAckBatch(submissionIds: string): Observable<ApiResponse> {
    const params = new HttpParams({ fromObject: { submissionIds } });
    return this.httpClient.get<ApiResponse>(`${this.baseURLs.efile}${URLS.POLL_ACK_BATCH}`, { params });
  }

  /** GET {efile}/downloadSelectedAckg?submissionIds=csv — zipped acks download. */
  downloadBatchAcks(submissionIds: string): Observable<ArrayBuffer> {
    const params = new HttpParams({ fromObject: { submissionIds } });
    return this.httpClient.get(`${this.baseURLs.efile}${URLS.DOWNLOAD_BATCH_ACKS}`, {
      params,
      responseType: 'arraybuffer'
    });
  }

  /** GET {efile}/submitBatch?subIds=csv&env=TEST|PROD — bulk submission. */
  submitBatch(submissionIds: string, env: string): Observable<ApiResponse> {
    const params = new HttpParams({ fromObject: { subIds: submissionIds, env } });
    return this.httpClient.get<ApiResponse>(`${this.baseURLs.efile}${URLS.SUBMIT_BATCH}`, { params });
  }

  /**
   * GET {efile}/Generate8453CPdfContent?subId=X — returns the 8453-C PDF blob.
   * Mirrors `efileFactory.download8453cPdf` (EfileService.js L556).
   */
  download8453cPdf(submissionId: string | number): Observable<ArrayBuffer> {
    const params = new HttpParams({ fromObject: { subId: String(submissionId) } });
    return this.httpClient.get(`${this.baseURLs.efile}${URLS.GENERATE_8453C_PDF}`, {
      params,
      responseType: 'arraybuffer'
    });
  }

  /**
   * GET {efile}/Generate8879CPdfContent?subId=X — returns the 8879-CF PDF blob.
   * Mirrors `efileFactory.download8879cfPdf` (EfileService.js L565).
   */
  download8879cfPdf(submissionId: string | number): Observable<ArrayBuffer> {
    const params = new HttpParams({ fromObject: { subId: String(submissionId) } });
    return this.httpClient.get(`${this.baseURLs.efile}${URLS.GENERATE_8879C_PDF}`, {
      params,
      responseType: 'arraybuffer'
    });
  }
}
